import { iconsSVG } from "components/Topology/staticLayer/IconsSVG";
import { topologyColors } from "components/Topology/configs/topologyColors";
import { topologyConfig } from "components/Topology/configs/topologyConfig";

const { gray400, gray700, blue500 } = topologyColors;
const { nodeWidth, nodeHeight } = topologyConfig;

const NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

function ensureDefs(rootSvg) {
  let defs = rootSvg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS(NS, "defs");

    if (rootSvg.firstChild) {
      rootSvg.insertBefore(defs, rootSvg.firstChild);
    } else {
      rootSvg.appendChild(defs);
    }
  }
  return defs;
}

export function ensureIconSymbol(rootSvg, name) {
  const defs = ensureDefs(rootSvg);
  const symbolId = `icon-${name}`;
  if (!defs.querySelector(`#${symbolId}`)) {
    const s = document.createElementNS(NS, "symbol");
    s.setAttribute("id", symbolId);
    s.setAttribute("viewBox", "0 0 32 32");

    s.innerHTML = iconsSVG(name, undefined);
    defs.appendChild(s);
  }
  return symbolId;
}

export function ensureNodeSymbol(
  rootSvg,
  kind,
  paramsByKind,
  isPrint = false,
  iconName = null,
) {
  const defs = ensureDefs(rootSvg);
  const symbolId = `node-${kind}${isPrint ? "_print" : ""}`;
  if (defs.querySelector(`#${symbolId}`)) {
    return symbolId;
  }

  const s = document.createElementNS(NS, "symbol");
  s.setAttribute("id", symbolId);

  const width = paramsByKind.title?.width ?? nodeWidth;
  const height = paramsByKind.title?.height ?? nodeHeight;
  s.setAttribute("viewBox", `0 0 ${width} ${height}`);

  const rectBg = document.createElementNS(NS, "rect");
  rectBg.setAttribute("x", "0");
  rectBg.setAttribute("y", "0");
  rectBg.setAttribute("width", `${width}`);
  rectBg.setAttribute("height", `${height}`);
  if (paramsByKind.title?.fill) {
    rectBg.setAttribute("fill", paramsByKind.title.fill);
  }
  if (paramsByKind.title?.stroke) {
    rectBg.setAttribute("stroke", paramsByKind.title.stroke);
  }
  if (paramsByKind.title?.strokeWidth) {
    rectBg.setAttribute("stroke-width", paramsByKind.title.strokeWidth);
  }
  s.appendChild(rectBg);

  if (paramsByKind.icon) {
    const iconBg = document.createElementNS(NS, "rect");
    iconBg.setAttribute("x", "0");
    iconBg.setAttribute("y", "0");
    iconBg.setAttribute("width", `${paramsByKind.icon.width ?? height}`);
    iconBg.setAttribute("height", `${paramsByKind.icon.height ?? height}`);
    if (paramsByKind.icon.fill) {
      iconBg.setAttribute("fill", paramsByKind.icon.fill);
    }
    if (paramsByKind.icon.stroke) {
      iconBg.setAttribute("stroke", paramsByKind.icon.stroke);
    }
    s.appendChild(iconBg);

    if (iconName) {
      ensureIconSymbol(rootSvg, iconName);
      const use = document.createElementNS(NS, "use");

      use.setAttributeNS(XLINK_NS, "href", `#icon-${iconName}`);
      use.setAttribute("href", `#icon-${iconName}`);
      use.setAttribute("x", "10");
      use.setAttribute("y", "10");
      use.setAttribute("width", "32");
      use.setAttribute("height", "32");
      s.appendChild(use);
    }
  }

  defs.appendChild(s);
  return symbolId;
}

export function appendUse(
  rootSvgOrGroup,
  symbolId,
  x,
  y,
  width,
  height,
  opts = {},
) {
  const use = document.createElementNS(NS, "use");

  use.setAttributeNS(XLINK_NS, "href", `#${symbolId}`);
  use.setAttribute("href", `#${symbolId}`);
  if (x != null) {
    use.setAttribute("x", `${x}`);
  }
  if (y != null) {
    use.setAttribute("y", `${y}`);
  }
  if (width != null) {
    use.setAttribute("width", `${width}`);
  }
  if (height != null) {
    use.setAttribute("height", `${height}`);
  }
  if (opts.fill) {
    use.setAttribute("fill", opts.fill);
  }
  if (opts.className) {
    use.setAttribute("class", opts.className);
  }
  rootSvgOrGroup.appendChild(use);
  return use;
}

export const renderPrimitivesMethods = {
  ensureDefs,
  ensureIconSymbol,
  ensureNodeSymbol,
  appendUse,

  drawLine(
    ref,
    startX,
    startY,
    endX,
    endY,
    { stroke = gray700, isWireless } = {},
  ) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", `M${startX} ${startY} L${endX} ${endY}`);
    path.setAttribute("stroke", isWireless ? blue500 : stroke);
    path.setAttribute("fill", "none");
    if (isWireless) {
      path.setAttribute("stroke-dasharray", "5,2");
    }
    ref.appendChild(path);
    return path;
  },

  drawRect(
    ref,
    startX,
    startY,
    {
      width = nodeWidth,
      height = nodeHeight,
      stroke,
      strokeWidth = 2,
      fill,
    } = {},
  ) {
    const rect = document.createElementNS(NS, "rect");
    rect.setAttribute("x", `${startX}`);
    rect.setAttribute("y", `${startY}`);
    rect.setAttribute("width", `${width}`);
    rect.setAttribute("height", `${height}`);
    if (stroke) {
      rect.setAttribute("stroke", stroke);
    }
    rect.setAttribute("stroke-width", `${strokeWidth}`);
    if (fill) {
      rect.setAttribute("fill", fill);
    }
    ref.appendChild(rect);
    return rect;
  },

  drawIcon(ref, startX, startY, { size = 32, color = gray400, name } = {}) {
    if (!name) {
      return null;
    }
    const rootSvg =
      ref.ownerSVGElement ||
      (ref.nodeName === "svg" ? ref : null) ||
      document.querySelector("svg");
    if (!rootSvg) {
      const tmpSvg = document.createElementNS(NS, "svg");
      tmpSvg.setAttribute("x", `${startX}`);
      tmpSvg.setAttribute("y", `${startY}`);
      tmpSvg.setAttribute("width", `${size}`);
      tmpSvg.setAttribute("height", `${size}`);
      tmpSvg.innerHTML = iconsSVG(name, color);
      ref.appendChild(tmpSvg);
      return tmpSvg;
    }

    ensureIconSymbol(rootSvg, name);

    const use = document.createElementNS(NS, "use");
    use.setAttributeNS(XLINK_NS, "href", `#icon-${name}`);
    use.setAttribute("href", `#icon-${name}`);
    use.setAttribute("x", `${startX}`);
    use.setAttribute("y", `${startY}`);
    use.setAttribute("width", `${size}`);
    use.setAttribute("height", `${size}`);
    if (color) {
      use.setAttribute("fill", color);
    }
    ref.appendChild(use);
    return use;
  },

  drawText(
    ref,
    x,
    y,
    text = "",
    {
      ff = "ModeratW01-75Bd, Moderat, Arial, Lucida Grande",
      fs = 14,
      fw = "regular",
      color = gray700,
      dominantBaseline,
      textAnchor,
      alignmentBaseline,
      transform,
    } = {},
  ) {
    const tagText = document.createElementNS(NS, "text");
    tagText.setAttribute("x", `${x}`);
    tagText.setAttribute("y", `${y}`);
    tagText.setAttribute("font-family", ff);
    tagText.setAttribute("font-size", `${fs}`);
    tagText.setAttribute("font-weight", fw);
    tagText.setAttribute("fill", color);
    if (dominantBaseline) {
      tagText.setAttribute("dominant-baseline", dominantBaseline);
    }
    if (textAnchor) {
      tagText.setAttribute("text-anchor", textAnchor);
    }
    if (alignmentBaseline) {
      tagText.setAttribute("alignment-baseline", alignmentBaseline);
    }
    if (transform) {
      tagText.setAttribute("transform", transform);
    }
    tagText.textContent = text;
    ref.appendChild(tagText);
    return tagText;
  },
};

export default renderPrimitivesMethods;
